import React, {
  forwardRef,
  useRef,
  useState,
  useImperativeHandle,
  useEffect,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import SnTree from "@shared/components/Grid/SnTree";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import CommonSpinner from "@widgets/CommonSpinner";
import { getInitFormData } from "@shared/utils/CommonUtils";
import {
  ControlledTreeEnvironment,
  Tree,
  TreeItem,
  TreeItemIndex,
  DraggingPosition,
  DraggingPositionBetweenItems,
  DraggingPositionItem,
} from "react-complex-tree";
import "react-complex-tree/lib/style-modern.css";
import _ from "lodash";

interface Props {
  Menuprops: menuInfo;
}

interface ModifiedNodeInfo {
  isDirty: boolean;
  status: "INSERT" | "UPDATE" | string;
}

type NodeStatus = "INSERT" | "UPDATE" | "DELETE";

interface ChangeInfo {
  isDirty: boolean;
  status: NodeStatus;
}

interface TreeItemData extends TreeItem {
  index: TreeItemIndex;
  isFolder?: boolean;
  children: TreeItemIndex[];
  // data: any;
  data: {
    title: string;
    originTree?: string;
    [key: string]: any;
  };
  hasChildren?: boolean;
  canMove?: boolean;
  canRename?: boolean;
  isExpanded: boolean;
  modifiedNode?: ModifiedNodeInfo;
}

type TreeData = Record<TreeItemIndex, TreeItemData>;

/** 회사메뉴관리 */
const SN_SYS_BAS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const [treeDataSource, setTreeDataSource] = useState<any>();
  const [treeDataSource2, setTreeDataSource2] = useState<any>();
  const [treeOriginalData, setTreeOriginalData] = useState<any>();
  const [treeOriginalData2, setTreeOriginalData2] = useState<any>();
  const treeOriginalData2Ref = useRef<any>();
  const treeGridApiRef = useRef<any>(null);
  const treeGridApiRef2 = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [allExpanded, setAllExpanded] = useState(true);
  const [allExpanded2, setAllExpanded2] = useState(true);
  const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>([
    "root_1",
  ]);
  const [expandedItems2, setExpandedItems2] = useState<TreeItemIndex[]>([
    "root_2",
  ]);
  const [mergedItems, setMergedItems] = useState<Record<string, TreeItemData>>(
    {}
  );
  const [contextMenuPos, setContextMenuPos] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [contextMenuNode, setContextMenuNode] = React.useState<any>(null);
  const [dragData, setDragData] = useState<any>(null);

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_SYS_BAS_REG_002";
  const defaultSize = { panel1width: "49.5%", panel2width: "49.5%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize); // SnResizable들의 크기
  //#endregion

  const openContextMenu = (event: React.MouseEvent, node: any) => {
    event.preventDefault();
    setSelectedItems([node]);
    setContextMenuPos({ x: event.clientX, y: event.clientY });
    setContextMenuNode(node);
  };

  const closeContextMenu = () => {
    setContextMenuPos(null);
    setContextMenuNode(null);
  };

  const [selectedItems, setSelectedItems] = useState<TreeItemIndex[]>([]);
  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();

        await new Promise((resolve) => setTimeout(resolve, 0));
        setLoading(true);
        await onSearch();
        setLoading(false);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (treeDataSource?.items) {
      const initiallyExpanded = Object.values(treeDataSource.items)
        .filter((item: any) => item?.isFolder && item?.isExpanded)
        .map((item: any) => item?.index.toString());

      setExpandedItems(initiallyExpanded);
    }
  }, [treeDataSource]);

  useEffect(() => {
    if (treeDataSource2?.items) {
      const initiallyExpanded = Object.values(treeDataSource2.items)
        .filter((item: any) => item?.isFolder && item?.isExpanded)
        .map((item: any) => item?.index.toString());

      setExpandedItems2(initiallyExpanded);
    }
  }, [treeDataSource2]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
  }));

  //#region onSearch
  const onSearch = async () => {
    try {
      const params = {
        id: "sn_sys_bas_reg_002_01",
        params: { cd_firm: userInfo.cd_firm },
      };

      setTreeDataSource([]);
      setTreeDataSource2([]);
      setSelectedItems([]);
      setDragData([]);
      const data: any = await fnGetDataSet(params);

      // const grid1Data = toNestedTree(transformMenuData(data[0]));
      // const grid2Data = toNestedTree(transformMenuData(data[1]));

      const grid1Data = toComplexTreeData(data[0], "root_1");
      const grid2Data = toComplexTreeData(data[1], "root_2");

      const addOriginTreeToItems = (
        items: Record<string, TreeItemData>,
        originTree: string
      ) => {
        const newItems: Record<string, TreeItemData> = {};
        for (const key in items) {
          newItems[key] = {
            ...items[key],
            data: {
              ...items[key].data,
              originTree, // 여기 추가
            },
          };
        }
        return newItems;
      };

      const mergedItems = {
        ...addOriginTreeToItems(grid1Data.items, "SN_SYS_BAS_REG_002_tree1"),
        ...addOriginTreeToItems(grid2Data.items, "SN_SYS_BAS_REG_002_tree2"),
        root_1: {
          ...grid1Data.items["root_1"],
          data: {
            ...grid1Data.items["root_1"].data,
            originTree: "SN_SYS_BAS_REG_002_tree1",
          },
        },
        root_2: {
          ...grid2Data.items["root_2"],
          data: {
            ...grid2Data.items["root_2"].data,
            originTree: "SN_SYS_BAS_REG_002_tree2",
          },
        },
        root: {
          index: "root",
          children: ["root_1", "root_2"],
          data: { title: "전체 메뉴" },
          hasChildren: true,
          isFolder: true,
          isExpanded: true,
          canMove: false,
          canRename: false,
        },
      };
      setMergedItems(mergedItems);

      setTreeDataSource(grid1Data);
      setTreeDataSource2(grid2Data);
      setTreeOriginalData(grid1Data);
      setTreeOriginalData2(grid2Data);

      const deepCopyGrid2 = JSON.parse(JSON.stringify(grid2Data));
      setTreeOriginalData2(deepCopyGrid2);
      treeOriginalData2Ref.current = deepCopyGrid2;
      setAllExpanded(false);
      setAllExpanded2(false);
      treeGridApiRef.current.resetUpdate();
      treeGridApiRef2.current.resetUpdate();
    } catch (error) {}
  };

  useEffect(() => {
    if (!treeDataSource?.items || !treeDataSource2?.items) return;

    // const fixedTree1 = fixFolderFlags(treeDataSource.items);
    // const fixedTree2 = fixFolderFlags(treeDataSource2.items);
    const fixedTree1 = treeDataSource.items;
    const fixedTree2 = treeDataSource2.items;

    const merged = {
      ...fixedTree1,
      ...fixedTree2,
      root_1: fixedTree1["root_1"],
      root_2: fixedTree2["root_2"],
      root: {
        index: "root",
        children: ["root_1", "root_2"],
        data: { title: "전체 메뉴" },
        hasChildren: true,
        isFolder: true,
        isExpanded: true,
        canMove: false,
        canRename: false,
      },
    };

    setMergedItems(merged);

    setExpandedItems((prev) =>
      prev.filter(
        (id) => fixedTree1[id] && (fixedTree1[id].children?.length ?? 0) > 0
      )
    );
    setExpandedItems2((prev) =>
      prev.filter(
        (id) => fixedTree2[id] && (fixedTree2[id].children?.length ?? 0) > 0
      )
    );
  }, [treeDataSource, treeDataSource2]);

  const toComplexTreeData = (
    flatData: any[],
    rootId: string
  ): { rootItem: string; items: Record<string, TreeItemData> } => {
    const items: Record<string, TreeItemData> = {};
    const rootChildren: string[] = [];

    // 트리별 프리픽스 설정
    const prefix =
      rootId === "root_1"
        ? "SN_SYS_BAS_REG_002_tree1_"
        : rootId === "root_2"
          ? "SN_SYS_BAS_REG_002_tree2_"
          : "unknown_";

    flatData.forEach((item) => {
      // id에 프리픽스 붙이기
      const id = prefix + String(item.cd_menu);
      const parentId = item.cd_menu_parent
        ? prefix + String(item.cd_menu_parent)
        : null;

      items[id] = {
        index: id,
        data: {
          title: ` ${item.nm_menu}`,
          subtitle: `레벨: ${item.no_level} / 순서: ${item.no_seq}`,
          // original: item,
          original: {
            ...item,
            no_pos: item.no_pos ?? "000", // 없거나 undefined/null이면 "000"으로
          },
          originTree:
            rootId === "root_1"
              ? "SN_SYS_BAS_REG_002_tree1"
              : rootId === "root_2"
                ? "SN_SYS_BAS_REG_002_tree2"
                : "unknown",
        },
        children: [],
        isFolder: item.no_level === 0 ? true : item.fg_menu === "MGM",
        canMove: true,
        canRename: false,
        isExpanded: item.no_level === 0,
        hasChildren: item.fg_menu === "MGM",
      };

      if (!parentId) {
        rootChildren.push(id);
      } else {
        if (!items[parentId]) {
          items[parentId] = {
            index: parentId,
            data: { title: "(placeholder)" },
            children: [],
            isFolder: true,
            isExpanded: true,
            canMove: true,
            canRename: false,
            hasChildren: true,
          } as TreeItemData;
        }
        items[parentId].children.push(id);
        items[parentId].isFolder = true;
        items[parentId].hasChildren = true;
      }
    });

    items[rootId] = {
      index: rootId,
      data: { title: "Root" },
      children: rootChildren,
      isFolder: true,
      isExpanded: true,
      canMove: false,
      canRename: false,
      hasChildren: rootChildren.length > 0,
    };

    return {
      rootItem: rootId,
      items,
    };
  };

  const handleDropToTree2 = async (
    draggedItems: TreeItemData[],
    target: any, // DraggingPosition (any로 받아서 안전하게 검사)
    tree2Items: TreeData,
    setTree2Wrapper: (newState: any) => void,
    tree1Items: TreeData,
    setTree1Wrapper: (newState: any) => void
  ) => {
    if (!draggedItems || draggedItems.length === 0) return;

    if (
      target.parentItem &&
      target.parentItem.includes("root") &&
      target.targetType == "between-items"
    ) {
      await showDialog(
        "alter",
        "해당 레벨로는 권한을 추가 할 수 없습니다. \n하위레벨로 추가해주시기 바랍니다."
      );
      return; // "root"라는 글자가 포함되어 있으면 종료
    }

    const draggedIds: TreeItemIndex[] = draggedItems.map((it) => it.index);

    const fromTree1 = draggedItems.some(
      (it) =>
        typeof it.index === "string" &&
        it.data.originTree == "SN_SYS_BAS_REG_002_tree1"
    );
    const fromTree2 = draggedItems.some(
      (it) =>
        typeof it.index === "string" &&
        it.data.originTree == "SN_SYS_BAS_REG_002_tree2"
    );

    // ------------------ 드래그 이동 처리 ------------------
    if (fromTree1 && !fromTree2) {
      const draggedCdMenus = draggedItems.map(
        (item) => item.data.original.cd_menu
      );

      const getNodeAndAllChildren = (
        treeItems: TreeData,
        node: TreeItemData
      ): TreeItemData[] => {
        let result: TreeItemData[] = [node]; // 자기 자신 포함

        (node.children || []).forEach((childId) => {
          const childNode = treeItems[childId];
          if (childNode) {
            result = result.concat(getNodeAndAllChildren(treeItems, childNode));
          }
        });

        return result;
      };

      // handleDropToTree2 안에서
      if (!draggedItems || draggedItems.length === 0) return;

      // 1️⃣ 선택한 노드 + 자식들 모두 찾아서 dragData용 배열 생성
      let allNodesToDrag: TreeItemData[] = [];
      draggedItems.forEach((item) => {
        const nodesWithChildren = getNodeAndAllChildren(tree1Items, item);
        allNodesToDrag = allNodesToDrag.concat(nodesWithChildren);
      });

      // 2️⃣ dragData에 중복 없이 누적
      const prevDragData = dragData || [];
      const newDragData = [
        ...prevDragData,
        ...allNodesToDrag.filter(
          (item) => !prevDragData.some((e: any) => e.index === item.index)
        ),
      ];

      // 3️⃣ 상태 업데이트
      setDragData(newDragData);

      const duplicateNode = Object.values(tree2Items).find((node: any) =>
        draggedCdMenus.includes(node.data?.original?.cd_menu)
      );

      if (duplicateNode) {
        await showDialog("alter", "이미 등록된 메뉴입니다.");
        return;
      }
      const preservedExpandedItems = [...expandedItems];
      const preservedExpandedItems2 = [...expandedItems2];
      const newTree1 = cloneTree(tree1Items);
      const newTree2 = cloneTree(tree2Items);

      // tree1에서 제거
      removeFromChildren(newTree1, draggedIds);

      draggedIds.forEach((id) => {
        deepCopyNodeAndChildren(
          tree1Items,
          newTree2,
          id,
          "SN_SYS_BAS_REG_002_tree2"
        );
      });
      // tree2에 복사해서 추가
      // draggedItems.forEach((item) => {
      //   const newItem = {
      //     ...(item as any),
      //     data: { ...item.data, originTree: "SN_SYS_BAS_REG_002_tree2" },
      //     children: Array.isArray(item.children) ? [...item.children] : [],
      //   };
      //   newTree2[item.index] = newItem;
      // });

      const { parentId, position } = resolveTargetParentAndPosition(
        target,
        newTree2
      );

      const newDraggedIds = draggedIds.map((id) =>
        typeof id === "string" ? id.replace("tree1", "tree2") : id
      );

      insertIntoChildren(newTree2, parentId, newDraggedIds, position);

      const affectedNodes = getAllDescendantIds(newTree2, newDraggedIds);
      recalcLevelsAndSeq(newTree2, "root_2", "INSERT", [], affectedNodes);
      // recalcLevelsAndSeq(newTree2, "root_2", "INSERT");
      updateDcPathRecursive(newTree2, "root_2");

      preservedExpandedItems.forEach((id) => {
        if (newTree1[id]) {
          newTree1[id].isExpanded = true;
        }
      });
      preservedExpandedItems2.forEach((id) => {
        if (newTree2[id]) {
          newTree2[id].isExpanded = true;
        }
      });
      setTree1Wrapper((prev: any) => ({ ...(prev || {}), items: newTree1 }));
      setTree2Wrapper((prev: any) => ({ ...(prev || {}), items: newTree2 }));
      setExpandedItems(preservedExpandedItems);
      setExpandedItems2(preservedExpandedItems2);
      return;
    }

    if (!fromTree1 && fromTree2) {
      const preservedExpandedItems2 = [...expandedItems2];
      const newTree2 = cloneTree(tree2Items);

      const { parentId, position } = resolveTargetParentAndPosition(
        target,
        newTree2
      );

      const nodesToMove = draggedIds.map((id) => {
        const node = { ...newTree2[id] }; // 객체 복사
        if (node.data?.original) {
          node.data.original.cd_menu_parent = parentId;
        }
        return node;
      });
      // 기존 children에서 드래그된 아이템 제거
      removeFromChildren(newTree2, draggedIds);

      // 자기 자신의 하위에 드롭 방지
      for (const draggedId of draggedIds) {
        const ancestorCheck = (candidate: TreeItemIndex | null): boolean => {
          if (!candidate) return false;
          if (candidate === draggedId) return true;
          // climb up
          const p = findParent(newTree2, candidate);
          return ancestorCheck(p);
        };

        if (ancestorCheck(parentId)) {
          console.warn(
            "Cannot drop into its own descendant. Ignoring move.",
            draggedId,
            parentId
          );
          return;
        }
      }
      nodesToMove.forEach((node) => {
        newTree2[node.index] = node; // 트리 객체에 다시 등록
      });
      insertIntoChildren(newTree2, parentId, draggedIds, position);

      const affectedNodes = getAllDescendantIds(newTree2, draggedIds);
      const isInsertMode = draggedIds.some(
        (id) => !treeOriginalData2?.items?.[id]
      );

      if (isInsertMode) {
        recalcLevelsAndSeq(newTree2, "root_2", "INSERT", [], affectedNodes);
      } else {
        recalcLevelsAndSeq(newTree2, "root_2", "UPDATE", [], affectedNodes);
      }
      // recalcLevelsAndSeq(newTree2, "root_2", "UPDATE");
      updateDcPathRecursive(newTree2, "root_2");
      preservedExpandedItems2.forEach((id) => {
        if (newTree2[id]) {
          newTree2[id].isExpanded = true;
        }
      });
      setTree2Wrapper((prev: any) => ({ ...(prev || {}), items: newTree2 }));
      setExpandedItems2(preservedExpandedItems2);
    }
  };

  const cloneTree = (items: TreeData): TreeData => {
    const copy: TreeData = {};
    Object.entries(items || {}).forEach(([k, v]) => {
      copy[k] = {
        ...(v as any),
        children: Array.isArray(v.children) ? [...v.children] : [],
      };
    });
    return copy;
  };

  // const removeFromChildren = (treeItems: TreeData, ids: TreeItemIndex[]) => {
  //   Object.values(treeItems).forEach((item) => {
  //     if (item.children) {
  //       item.children = item.children.filter(
  //         (childId) => !ids.includes(childId)
  //       );
  //     }
  //   });
  // };
  const removeFromChildren = (treeItems: TreeData, ids: TreeItemIndex[]) => {
    // ids에 부모와 자식 모두 포함되도록 확장
    const allIds = new Set<TreeItemIndex>();

    const addDescendants = (id: TreeItemIndex) => {
      allIds.add(id);
      const node = treeItems[id];
      if (node?.children) {
        node.children.forEach((childId) => addDescendants(childId));
      }
    };

    ids.forEach((id) => addDescendants(id));

    // 1️⃣ 부모 children 배열에서 제거
    Object.values(treeItems).forEach((item) => {
      if (item.children) {
        item.children = item.children.filter((childId) => !allIds.has(childId));
      }
    });

    // 2️⃣ treeItems에서 노드 자체 삭제
    allIds.forEach((id) => {
      delete treeItems[id];
    });
  };

  const insertIntoChildren = (
    treeItems: TreeData,
    parentId: TreeItemIndex | null,
    ids: TreeItemIndex[],
    position?: number
  ) => {
    const pid = parentId ?? "root_2";
    if (!treeItems[pid]) {
      console.warn("insertIntoChildren: parent not found", pid);
      return;
    }
    if (!treeItems[pid].children) treeItems[pid].children = [];

    // 중복 제거
    treeItems[pid].children = treeItems[pid].children.filter(
      (id) => !ids.includes(id)
    );

    let insertIndex = treeItems[pid].children.length;
    if (typeof position === "number" && position >= 0) {
      insertIndex = Math.min(position, treeItems[pid].children.length);
    }

    treeItems[pid].children.splice(insertIndex, 0, ...ids);
  };

  // helper: 특정 childId의 직접 부모를 찾아 반환 (없으면 null)
  const findParent = (
    treeItems: TreeData,
    childId: TreeItemIndex
  ): TreeItemIndex | null => {
    for (const [key, val] of Object.entries(treeItems)) {
      if (Array.isArray(val.children) && val.children.indexOf(childId) !== -1) {
        return key;
      }
    }
    return null;
  };

  // 레벨과 순서 재계산
  const recalcLevelsAndSeq = (
    treeItems: TreeData,
    rootId: TreeItemIndex,
    status: string | null = null, // null이면 상태 변경 안 함
    parentPath: string[] = [],
    affectedNodes: Set<TreeItemIndex> | null = null // 상태 변경할 노드 집합
  ) => {
    const dfs = (
      nodeId: TreeItemIndex,
      parentId: TreeItemIndex | null,
      level: number
    ) => {
      const node = treeItems[nodeId];
      if (!node) return;

      const parent = parentId ? treeItems[parentId] : null;
      const siblings = parent
        ? parent.children
        : treeItems[rootId]?.children || [];
      const order = siblings.indexOf(nodeId);

      node.data.original.no_level = level;
      node.data.original.cd_menu_parent = parentId;
      node.data.original.no_seq = order + 1;

      // if (affectedNodes && affectedNodes.has(nodeId)) {
      //   if (status === "INSERT") {
      //     node.modifiedNode = { isDirty: true, status };
      //   }
      // } else if (status === "UPDATE") {
      //   node.modifiedNode = { isDirty: true, status };
      // }

      (node.children || []).forEach((childId) => {
        dfs(childId, nodeId, level + 1);
      });
    };

    (treeItems[rootId]?.children || []).forEach((childId) =>
      dfs(childId, rootId, 0)
    );
  };
  // -------------- 부모 ID와 position(위치) 계산 로직 --------------
  // 목적:
  // - 아이템 "위에" 드롭 (targetType === 'on-item' / 'on-folder') → parentId = 해당 아이템
  // - 아이템 "사이"에 드롭 → parentId = 타겟 아이템의 부모
  // - childIndex가 제공되면 그 값을 position으로 사용
  // - 위 정보가 없으면 findParent로 부모를 찾아서 처리
  const resolveTargetParentAndPosition = (
    targetObj: any,
    treeItems: TreeData
  ): { parentId: TreeItemIndex | null; position: number | undefined } => {
    let parentId: TreeItemIndex | null = null;
    let position: number | undefined = undefined;

    const tt: string | undefined =
      targetObj?.targetType || targetObj?.type || undefined;
    const targetItem: string | undefined =
      targetObj?.targetItem ??
      targetObj?.item ??
      targetObj?.target ??
      undefined;
    const parentItemField: string | undefined =
      targetObj?.parentItem ?? undefined;
    const childIndexField: number | undefined =
      typeof targetObj?.childIndex === "number"
        ? targetObj.childIndex
        : undefined;
    const linePosition: string | undefined =
      targetObj?.linePosition ?? undefined;

    // 1) 우선순위: childIndex가 있으면 position으로 사용
    if (typeof childIndexField === "number") {
      position = childIndexField;
    } else if (typeof targetObj?.position === "number") {
      position = targetObj.position;
    }

    if (tt) {
      const t = tt.toString();

      if (t === "item" || t === "on-item") {
        // 'item' 또는 'on-item' 일 때는 targetItem을 부모로 쓰자
        parentId = targetItem ?? null;
        if (
          position === undefined &&
          parentId &&
          treeItems[parentId] &&
          Array.isArray(treeItems[parentId].children)
        ) {
          position = treeItems[parentId].children.length;
        }
      } else if (t === "on-folder") {
        parentId = targetItem ?? parentItemField ?? null;
        if (
          position === undefined &&
          parentId &&
          treeItems[parentId] &&
          Array.isArray(treeItems[parentId].children)
        ) {
          position = treeItems[parentId].children.length;
        }
      } else if (t.startsWith("on")) {
        parentId = targetItem ?? parentItemField ?? null;
        if (
          position === undefined &&
          parentId &&
          treeItems[parentId] &&
          Array.isArray(treeItems[parentId].children)
        ) {
          position = treeItems[parentId].children.length;
        }
      } else if (t.includes("between")) {
        parentId = parentItemField ?? null;

        if (position === undefined) {
          if (targetItem) {
            const p = parentItemField ?? findParent(treeItems, targetItem);
            if (p && treeItems[p]) {
              const idx = treeItems[p].children.indexOf(targetItem);
              position = idx + (linePosition === "bottom" ? 1 : 0);
            }
          }
        }
      } else {
        parentId = parentItemField ?? targetItem ?? null;
        if (
          position === undefined &&
          parentId &&
          treeItems[parentId] &&
          Array.isArray(treeItems[parentId].children)
        ) {
          position = treeItems[parentId].children.length;
        }
      }
      // 기타 알 수 없는 타입 처리
    } else {
      // targetType이 없을 경우 fallback 처리
      if (parentItemField) {
        parentId = parentItemField;
      } else if (targetItem) {
        const p = findParent(treeItems, targetItem);
        if (p) {
          parentId = p;
          if (position === undefined) {
            position =
              treeItems[p].children.indexOf(targetItem) +
              (linePosition === "bottom" ? 1 : 0);
          }
        } else {
          parentId = "root_2"; // 최종 fallback
        }
      } else {
        parentId = "root_2"; // 최종 fallback
      }
    }

    // parentId가 undefined일 경우 최종 fallback
    if (parentId === undefined || parentId === null) {
      parentId = "root_2";
    }

    return { parentId, position };
  };

  const deepCopyNodeAndChildren = (
    sourceTree: TreeData,
    newTree: TreeData,
    nodeId: TreeItemIndex,
    newOriginTree: string
  ) => {
    const node = sourceTree[nodeId];
    if (!node) return;

    // 1️⃣ index(tree ID) 교체
    const newId =
      typeof nodeId === "string" ? nodeId.replace("tree1", "tree2") : nodeId;

    // 2️⃣ children ID 교체
    const newChildren: TreeItemIndex[] = (node.children || []).map((childId) =>
      typeof childId === "string" ? childId.replace("tree1", "tree2") : childId
    );

    // 3️⃣ original 깊은 복사
    const originalCopy = JSON.parse(JSON.stringify(node.data.original || {}));

    // 4️⃣ 새로운 노드 추가
    newTree[newId] = {
      ...node,
      index: newId,
      data: {
        ...node.data,
        originTree: newOriginTree,
        original: originalCopy,
      },
      children: newChildren,
    };

    // 5️⃣ 재귀적으로 자식 처리
    (node.children || []).forEach((childId) => {
      deepCopyNodeAndChildren(sourceTree, newTree, childId, newOriginTree);
    });
  };

  function getAllDescendantIds(
    treeItems: TreeData,
    nodeIds: TreeItemIndex[]
  ): Set<TreeItemIndex> {
    const allIds = new Set<TreeItemIndex>();

    function collectDescendants(id: TreeItemIndex) {
      if (allIds.has(id)) return;
      allIds.add(id);
      const node = treeItems[id];
      if (node?.children) {
        node.children.forEach(collectDescendants);
      }
    }

    nodeIds.forEach(collectDescendants);
    return allIds;
  }

  const updateDcPathRecursive = (
    treeItems: TreeData,
    nodeId: TreeItemIndex,
    parentPath: string[] = []
  ) => {
    const node = treeItems[nodeId];
    if (!node) return;

    let currentPath = parentPath;

    // original이 있는 경우만 dc_path 계산
    if (node.data?.original && node.data.original.no_level !== 0) {
      const currentCdMenu = node.data.original.cd_menu ?? "";
      currentPath = [...parentPath, currentCdMenu];
      node.data.original.dc_path = currentPath.join(",");
    }

    // 자식은 무조건 순회
    (node.children || []).forEach((childId) =>
      updateDcPathRecursive(treeItems, childId, currentPath)
    );
  };

  //#region onSave
  const onSave = async () => {
    try {
      // 트리별 변경된 노드만 추출
      // Extract only changed nodes by tree
      const changes = applyChangesToOriginal(
        treeOriginalData2Ref.current,
        // treeOriginalData2 || [],
        treeDataSource2 || []
      );
      const {
        insert: insertTree2,
        update: updateTree2,
        deleteNode: deleteTree2,
      } = getDirtyNodesFromTree(changes || []);

      // dirtyTree2를 getChange와 동일한 구조로 변환
      // Convert dirtyTree2 to the same structure as getChange
      const dirtyTree2Change = {
        modifiedRowcount:
          insertTree2.length + updateTree2.length + deleteTree2.length,
        insert: insertTree2,
        update: updateTree2,
        //delete: deletedNodesData,
        delete: deleteTree2,
      };

      if (dirtyTree2Change.modifiedRowcount === 0) {
        await showDialog("alter", "저장할 내용이 없습니다.");
        return;
      }

      // 저장 데이터 구성
      const saveData = {
        sys_firmmenu: dirtyTree2Change,
        // sys_mmenu: changesRow,
      };

      await fnSave("SN_SYS_BAS_REG_002_SAVE_01", saveData);
      // 저장 성공 시 트리의 isDirty 초기화
      // Initialize the tree's isDirty when saving is successful
      clearDirtyFlag(treeDataSource || []);
      clearDirtyFlag(treeDataSource2 || []);

      return true;
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  function getDirtyNodesFromTree(treeItems: { items: Record<string, any> }): {
    insert: any[];
    update: any[];
    deleteNode: any[];
  } {
    const insert: any[] = [];
    const update: any[] = [];
    const deleteNode: any[] = [];
    const pkSet = new Set<string>();

    const items = treeItems.items;

    Object.values(items).forEach((node) => {
      if (!node.data?.original || node.data.original.no_level === 0) return;

      const original = node.modifiedNode || node.data.original;

      if (original?.isDirty) {
        const pkKey = `${node.data?.original.cd_firm}_${node.data?.original.cd_user}_${node.data?.original.cd_menu}`;

        if (!pkSet.has(pkKey)) {
          pkSet.add(pkKey);

          const data = {
            ...node.data?.original,
            cd_menu_parent: removePrefix(node.data?.original.cd_menu_parent),
            dc_path: node.data?.original.dc_path
              ?.split(",")
              .map(removePrefix)
              .join(","),
            fg_select: "1",
            fg_insert: "Y",
            fg_update: "Y",
            fg_delete: "Y",
            fg_excel: "Y",
            fg_print: "Y",
            fg_setup: "Y",
            fg_exconn: "Y",
          };

          if (node.modifiedNode.status === "INSERT") {
            insert.push(data);
          } else if (node.modifiedNode.status === "UPDATE") {
            update.push(data);
          } else if (node.modifiedNode.status === "DELETE") {
            deleteNode.push(data);
          } else {
            console.warn("Unknown status for dirty node:", node.modifiedNode);
          }
        } else {
        }
      }
    });

    return { insert, update, deleteNode };
  }

  function applyChangesToOriginal(
    original: { items: Record<string, any> },
    current: { items: Record<string, any> }
  ) {
    const originalItems = original?.items || {};
    const currentItems = current?.items || {};

    // 1️⃣ 원본 아이템을 cd_menu → node Map으로
    const originalMap: Record<string, any> = {};
    Object.values(originalItems).forEach((node) => {
      const cd_menu = node.data?.original?.cd_menu;
      if (cd_menu) originalMap[cd_menu] = node;
    });

    Object.values(currentItems).forEach((node) => {
      const cd_menu = node.data?.original?.cd_menu;
      if (!cd_menu) return;

      if (!originalMap[cd_menu]) {
        // INSERT
        originalItems[node.index] = {
          ...node,
          modifiedNode: { isDirty: true, status: "INSERT" },
        };
      }
    });

    Object.values(currentItems).forEach((node) => {
      const cd_menu = node.data?.original?.cd_menu;
      if (!cd_menu) return;

      const origNode = originalMap[cd_menu];
      if (!origNode) return; // 원본에 없으면 INSERT에서 처리됨

      const nodeParent = node.data?.original?.cd_menu_parent;
      const origParent = origNode.data?.original?.cd_menu_parent;
      const nodeLevel = node.data?.original?.no_level;
      const nodeSeq = node.data?.original?.no_seq;
      const origLevel = origNode.data?.original?.no_level;
      const origSeq = origNode.data?.original?.no_seq;

      if (
        nodeLevel !== origLevel ||
        nodeSeq !== origSeq ||
        nodeParent !== origParent
      ) {
        // 1️⃣ modifiedNode 상태 추가
        origNode.modifiedNode = { isDirty: true, status: "UPDATE" };

        // 2️⃣ 실제 원본 데이터 업데이트
        origNode.data.original.no_level = nodeLevel;
        origNode.data.original.no_seq = nodeSeq;

        // 필요하면 path나 parent도 업데이트 가능
        origNode.data.original.cd_menu_parent =
          node.data.original.cd_menu_parent;
        origNode.data.original.dc_path = node.data.original.dc_path;
      }
    });

    // 3️⃣ 원본에는 있는데 현재(UI)에 없는 경우 → DELETE
    const currentCdSet = new Set(
      Object.values(currentItems)
        .map((node) => node.data?.original?.cd_menu)
        .filter(Boolean)
    );

    Object.values(originalItems).forEach((origNode) => {
      const cd_menu = origNode.data?.original?.cd_menu;
      if (!cd_menu) return;

      if (!currentCdSet.has(cd_menu)) {
        origNode.modifiedNode = { isDirty: true, status: "DELETE" };
      }
    });

    return original;
  }

  function removePrefix(id: string | undefined): string | undefined {
    if (!id) return id;
    return id.replace(
      /^(SN_SYS_BAS_REG_002_tree1_|SN_SYS_BAS_REG_002_tree2_)/,
      ""
    );
  }

  // 저장 후 isDirty 초기화
  function clearDirtyFlag(treeDataSource: { items: Record<string, any> }) {
    const items = treeDataSource.items;

    Object.values(items).forEach((node) => {
      if (node.data?.original) {
        node.data.original.isDirty = false;
        node.data.original.status = ""; // 상태 초기화
      }
      if (node.modifiedNode) {
        node.modifiedNode.isDirty = false;
        node.modifiedNode.status = "";
      }
    });
  }

  const onDeleteRow = (nodeId: string | number) => {
    // 삭제 대상 노드와 자식들
    const allIdsToDelete = new Set<TreeItemIndex>();
    getAllDescendants(treeDataSource2.items, nodeId).forEach((id) =>
      allIdsToDelete.add(id)
    );
    allIdsToDelete.add(nodeId);

    // 1️⃣ treeDataSource2 복사

    // const updatedTree = { ...treeDataSource2.items };
    const updatedTree = _.cloneDeep(treeDataSource2.items);

    // 2️⃣ 각 노드에 modifiedNode 상태 추가 & 삭제
    allIdsToDelete.forEach((id) => {
      if (updatedTree[id]) {
        updatedTree[id] = {
          ...updatedTree[id],
          // modifiedNode: { isDirty: true, status: "DELETE" },
        };
        delete updatedTree[id];
      }
    });

    // 3️⃣ 자식 배열 업데이트
    Object.values(updatedTree).forEach((item) => {
      if ((item as TreeItemData).children) {
        (item as TreeItemData).children = (
          item as TreeItemData
        ).children.filter((childId: any) => !allIdsToDelete.has(childId));
      }
    });

    restoreDeletedNodes(Array.from(allIdsToDelete));

    const preservedExpandedItems = expandedItems.filter(
      (id) => treeDataSource.items[id] // 삭제되지 않은 노드만
    );

    preservedExpandedItems.forEach((id) => {
      if (treeOriginalData.items[id]) {
        treeOriginalData.items[id].isExpanded = true;
      }
    });

    // 4️⃣ 열린 상태 보존
    const preservedExpandedItems2 = expandedItems2.filter(
      (id) => updatedTree[id]
    );
    preservedExpandedItems2.forEach((id) => {
      if (updatedTree[id]) updatedTree[id].isExpanded = true;
    });

    // 5️⃣ 상태 반영
    setTreeDataSource2((prev: any) => ({ ...prev, items: updatedTree }));
    setExpandedItems2(preservedExpandedItems2);
    // setTreeDataSource(treeOriginalData);
    // setTreeDataSource(_.cloneDeep(treeOriginalData));
  };

  const restoreDeletedNodes = (deletedNodeIds: TreeItemIndex[]) => {
    const updatedTree1 = _.cloneDeep(treeDataSource.items);

    // ✅ 부모까지 역재귀 복원 함수
    const restoreWithParents = (node: any) => {
      // 1️⃣ 노드 복원
      if (!updatedTree1[node.index]) {
        updatedTree1[node.index] = _.cloneDeep(node);
        console.log("복원된 노드:", node.index);
      }

      // 2️⃣ 부모 찾기
      const parentCdMenu = node?.data?.original?.cd_menu_parent;
      if (!parentCdMenu) return; // 루트면 종료

      const parentNode =
        dragData.find(
          (item: any) => item?.data?.original?.cd_menu === parentCdMenu
        ) ??
        Object.values(updatedTree1).find(
          (item: any) => item?.data?.original?.cd_menu === parentCdMenu
        );

      if (!parentNode) return;

      const parentIndex =
        typeof parentNode.index === "string"
          ? parentNode.index.replace("tree2", "tree1")
          : parentNode.index;

      // 3️⃣ 부모 복원 (없으면)
      if (!updatedTree1[parentIndex]) {
        updatedTree1[parentIndex] = _.cloneDeep(parentNode);
        console.log("복원된 부모 노드:", parentIndex);
      }

      // 4️⃣ 부모.children 연결
      const parent = updatedTree1[parentIndex] as any;
      parent.children = parent.children || [];

      // 이미 있으면 중복 추가 안함
      if (!parent.children.includes(node.index)) {
        // 🔑 복원할 노드의 no_seq
        const nodeSeq = node.data.original.no_seq;

        // 🔑 children에 삽입할 위치 찾기
        let insertIndex = parent.children.length; // 기본: 맨 뒤
        for (let i = 0; i < parent.children.length; i++) {
          const childId = parent.children[i];
          const childNode = updatedTree1[childId];
          if (childNode) {
            const childSeq = childNode.data.original.no_seq;
            if (nodeSeq < childSeq) {
              insertIndex = i;
              break;
            }
          }
        }

        // 🔑 원하는 위치에 삽입
        parent.children.splice(insertIndex, 0, node.index);

        console.log(
          "부모에 자식 순서대로 연결됨:",
          parent.index,
          "→",
          node.index,
          "(순서:",
          nodeSeq,
          ")"
        );
      }
      parent.isExpanded = true;
      // 5️⃣ 부모의 부모도 재귀 처리
      restoreWithParents(parent);
    };

    // ✅ 삭제된 노드들 복원 시작
    deletedNodeIds.forEach((deletedId) => {
      const tree1Index =
        typeof deletedId === "string"
          ? deletedId.replace("tree2", "tree1")
          : deletedId;

      const deletedNode = dragData?.find(
        (item: any) => item.index === tree1Index
      );
      if (deletedNode) restoreWithParents(deletedNode);
    });

    // ✅ children 정리 (안전하게)
    Object.values(updatedTree1).forEach((item: any) => {
      if (!item.children) item.children = [];
      item.children = item.children.filter(
        (childId: any) => updatedTree1[childId]
      );
    });

    console.log("최종 복원 트리1:", updatedTree1);
    setTreeDataSource((prev: any) => ({
      ...prev,
      items: updatedTree1,
    }));
  };

  const deleteNodes = (treeItems: TreeData, idsToDelete: TreeItemIndex[]) => {
    const newTree = { ...treeItems };

    const allIdsToDelete = new Set<TreeItemIndex>();
    idsToDelete.forEach((id) => {
      allIdsToDelete.add(id);
      getAllDescendants(treeItems, id).forEach((childId) =>
        allIdsToDelete.add(childId)
      );
    });

    Object.values(newTree).forEach((item) => {
      if (item.children) {
        item.children = item.children.filter(
          (childId) => !allIdsToDelete.has(childId)
        );
      }
    });

    allIdsToDelete.forEach((id) => {
      delete newTree[id];
    });

    return newTree;
  };

  const collectNodesToDelete = (
    treeItems: TreeData,
    idsToDelete: TreeItemIndex[]
  ) => {
    const allIdsToDelete = new Set<TreeItemIndex>();
    idsToDelete.forEach((id) => {
      allIdsToDelete.add(id);
      getAllDescendants(treeItems, id).forEach((childId) =>
        allIdsToDelete.add(childId)
      );
    });

    return Array.from(allIdsToDelete).map((id) => {
      const node = { ...treeItems[id] };
      node.modifiedNode = { isDirty: true, status: "DELETE" };
      return node;
    });
  };
  const getAllDescendants = (
    treeItems: TreeData,
    id: TreeItemIndex
  ): TreeItemIndex[] => {
    const node = treeItems[id];
    if (!node || !node.children) return [];
    return node.children.reduce(
      (acc, childId) => [
        ...acc,
        childId,
        ...getAllDescendants(treeItems, childId),
      ],
      [] as TreeItemIndex[]
    );
  };

  //#region syncBtn
  const onSyncMenu = async () => {
    const confirm = await showDialog(
      "confirm",
      "노츠 메뉴를 연동 하시겠습니까?"
    );
    if (!confirm) return;
    const params = {
      cd_firm: userInfo.cd_firm,
      id: "sn_sys_bas_reg_002_02",
      params: {},
    };
    setSyncLoading(true);
    const result = await callApi("SN_SYS_BAS_REG_002_btnSync", params);
    setSyncLoading(false);
    if (result.status) {
      await showDialog("success", "메뉴 동기화가 완료되었습니다.");
      onSearch();
    }
  };

  const toggleAll = (
    treeKey: "SN_SYS_BAS_REG_002_tree1" | "SN_SYS_BAS_REG_002_tree2"
  ) => {
    let items: Record<string, TreeItem> = {};
    let expand = false;

    if (treeKey === "SN_SYS_BAS_REG_002_tree1") {
      items = treeDataSource.items as Record<string, TreeItem>;
      expand = !allExpanded;
    } else if (treeKey === "SN_SYS_BAS_REG_002_tree2") {
      items = treeDataSource2.items as Record<string, TreeItem>;
      expand = !allExpanded2;
    }

    const newItems: Record<string, any> = {};

    Object.entries(items).forEach(([key, node]) => {
      const shouldKeepExpanded = !expand && node.data?.original?.no_level === 0;

      newItems[key] = {
        ...node,
        isExpanded: shouldKeepExpanded || expand,
      };
    });

    // 트리별로 set
    if (treeKey === "SN_SYS_BAS_REG_002_tree1") {
      setTreeDataSource((prev: any) => ({ ...prev, items: newItems }));
      setAllExpanded(expand);
    } else if (treeKey === "SN_SYS_BAS_REG_002_tree2") {
      setTreeDataSource2((prev: any) => ({ ...prev, items: newItems }));
      setAllExpanded2(expand);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex h-full gap-2">
        <CommonSpinner isLoading={loading} />
        <CommonSpinner isLoading={syncLoading} />
        {treeDataSource?.items?.root_1 && (
          <ControlledTreeEnvironment
            items={mergedItems}
            getItemTitle={(item) => item.data.title}
            canDragAndDrop={true}
            canDropAt={(items, target) => {
              // tree1에 드롭 불가
              if (
                "treeId" in target &&
                target.treeId === "SN_SYS_BAS_REG_002_tree1"
              ) {
                return false;
              }
              return true;
            }}
            canReorderItems={true}
            canDropOnFolder={true}
            canDropOnNonFolder={false}
            onSelectItems={(originTree) => {
              setSelectedItems(originTree); // 선택된 노드 ID 배열 저장
            }}
            viewState={{
              SN_SYS_BAS_REG_002_tree1: {
                selectedItems: selectedItems,
                expandedItems: expandedItems,
              },
              SN_SYS_BAS_REG_002_tree2: {
                selectedItems: selectedItems,
                expandedItems: expandedItems2,
              },
            }}
            onExpandItem={(item, treeId) => {
              if (treeId === "SN_SYS_BAS_REG_002_tree1") {
                setExpandedItems((prev) => [...prev, item.index]);
              } else if (treeId === "SN_SYS_BAS_REG_002_tree2") {
                setExpandedItems2((prev) => [...prev, item.index]);
              }
            }}
            onCollapseItem={(item, treeId) => {
              if (treeId === "SN_SYS_BAS_REG_002_tree1") {
                setExpandedItems((prev) =>
                  prev.filter((id) => id !== item.index)
                );
              } else if (treeId === "SN_SYS_BAS_REG_002_tree2") {
                setExpandedItems2((prev) =>
                  prev.filter((id) => id !== item.index)
                );
              }
            }}
            onDrop={(draggedItems, target) => {
              if (
                "treeId" in target &&
                target.treeId === "SN_SYS_BAS_REG_002_tree2"
              ) {
                handleDropToTree2(
                  draggedItems as TreeItemData[], // 1. 드래그된 아이템 배열
                  target, // 2. 드롭 위치 정보가 담긴 객체
                  treeDataSource2.items, // 3. 트리2의 현재 아이템 데이터 (TreeData 타입)
                  setTreeDataSource2, // 4. 트리2 상태를 업데이트하는 함수 (setState)
                  treeDataSource.items, // 5. 트리1의 현재 아이템 데이터 (TreeData 타입)
                  setTreeDataSource // 6. 트리1 상태를 업데이트하는 함수 (setState)
                );
              }
            }}
          >
            <SnResizable
              direction={"width"}
              seq={1}
              cd_menu={cd_menu}
              defaultSize={defaultSize}
              size={sizes.panel1width}
              onResizeStop={onResizeStop}
              enable={{ right: true }}
              minHeight={130}
            >
              <SnTab
                titles={["노츠메뉴"]}
                header={[
                  <SnButton
                    key="expand"
                    text={allExpanded ? "Collapse" : "Expand"}
                    onClick={() => toggleAll("SN_SYS_BAS_REG_002_tree1")}
                  />,
                ]}
                body={[
                  <div
                    key="SN_SYS_BAS_REG_002_tree1-container"
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      height: "100%",
                      overflow: "auto",
                    }}
                  >
                    <Tree
                      treeId="SN_SYS_BAS_REG_002_tree1"
                      rootItem="root_1"
                      treeLabel="Menu Tree"
                    />
                  </div>,
                ]}
              />
            </SnResizable>
            <SnResizable
              direction={"width"}
              seq={2}
              cd_menu={cd_menu}
              defaultSize={defaultSize}
              size={sizes.panel2width}
              onResizeStop={onResizeStop}
              enable={{ right: true }}
              minHeight={130}
            >
              <SnTab
                titles={["회사메뉴"]}
                header={[
                  <SnTabHeader
                    key="header2"
                    body={[
                      <SnButton text={"메뉴동기화"} onClick={onSyncMenu} />,
                      <SnButton
                        key="expand"
                        text={allExpanded2 ? "Collapse" : "Expand"}
                        onClick={() => toggleAll("SN_SYS_BAS_REG_002_tree2")}
                      />,
                    ]}
                  />,
                ]}
                body={[
                  <div
                    key="SN_SYS_BAS_REG_002_tree2-container"
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      height: "100%",
                      overflow: "auto",
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (selectedItems.length > 0) {
                        setContextMenuPos({ x: e.clientX, y: e.clientY });
                        setContextMenuNode(selectedItems[0].toString()); // ID 문자열 저장
                      }
                    }}
                    onClick={() => {
                      setContextMenuPos(null);
                      setContextMenuNode(null);
                    }}
                  >
                    <Tree
                      treeId="SN_SYS_BAS_REG_002_tree2"
                      rootItem="root_2"
                      treeLabel="Menu Tree"
                    />
                  </div>,
                ]}
              />
            </SnResizable>
          </ControlledTreeEnvironment>
        )}
        {contextMenuPos && (
          <div
            style={{
              position: "fixed",
              top: contextMenuPos.y,
              left: contextMenuPos.x,
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 1000,
              padding: "6px 10px",
              borderRadius: "4px",
              cursor: "pointer",
              fontFamily: "'Malgun Gothic', '맑은 고딕', Arial, sans-serif",
              fontSize: "13px",
              color: "#333",
              lineHeight: "1.6",
              transition: "background-color 0.2s ease",
            }}
            onMouseLeave={closeContextMenu} // 마우스가 메뉴를 떠나면 닫기
          >
            <div
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRow(selectedItems[0]);
                closeContextMenu();
              }}
            >
              메뉴 권한 제거
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default SN_SYS_BAS_REG_002;
